import { useMemo, useRef, useState } from "react";

import { Button } from "@krea/shared/design-system/Button";

import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";

import { ActivityListPane } from "../components/log-detail-list";
import { LogDetailPane } from "../components/log-detail-pane";
import { useGetListAuditLogsQuery } from "../hooks/data/use-get-list-audit-logs.query";
import type { IAuditLog } from "../types";

export const ApiLogsPage = () => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { data, isLoading, isError, refetch, hasNextPage, fetchNextPage } =
    useGetListAuditLogsQuery(Number(lenderCompany?.id));

  const [selectedItem, setSelectedItem] = useState<IAuditLog | null>(null);
  const [filters, setFilters] = useState<{ q?: string }>({});

  const mainContainer = useRef<HTMLDivElement>(null);

  const onSelect = (item: IAuditLog): void => {
    setSelectedItem(item);
  };

  const items = useMemo(() => {
    if (!data) return [];

    return data.pages.flatMap((page) => page.items);
  }, [data]);

  return (
    <main className="flex h-full w-full flex-col">
      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {/* Mobile: stacked (list first, detail below) */}
        <div
          ref={mainContainer}
          className="grid h-full grid-cols-1 lg:grid-cols-12"
        >
          <div
            className="lg:col-span-5 "
            style={{
              height: mainContainer.current?.clientHeight,
            }}
          >
            {isLoading && (
              <div className="p-6 text-sm text-black-100">
                Loading activity…
              </div>
            )}
            {isError && (
              <div className="p-6">
                <p className="font-semibold">Couldn’t load activity</p>
                <Button onClick={() => refetch()}>Retry</Button>
              </div>
            )}
            {items.length > 0 ? (
              <>
                <ActivityListPane
                  items={items}
                  selectedItem={selectedItem}
                  filters={filters}
                  onChangeFilters={setFilters}
                  onSelect={onSelect}
                />
                {hasNextPage && (
                  <Button onClick={() => fetchNextPage()}>Load more</Button>
                )}
              </>
            ) : (
              <div className="p-6 text-sm text-black-100">
                No activity found.
              </div>
            )}
          </div>

          <div
            className="overflow-y-auto lg:col-span-7"
            style={{
              height: mainContainer.current?.clientHeight,
            }}
          >
            {!selectedItem ? (
              <div className="p-6 text-sm text-black-100">
                Select an item to view details.
              </div>
            ) : (
              <LogDetailPane detail={selectedItem} />
            )}
          </div>
        </div>
      </div>
    </main>
  );
};
